import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    country: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    setFormData((prev) => ({ ...prev, country: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        message: `Country: ${formData.country}\n\n${formData.message}`,
      });
      if (response.data.status === "success") {
        setIsSubmitted(true);
        toast.success("Message sent successfully!");
        setFormData({ name: "", company: "", email: "", country: "", message: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const countries = [
    "United States",
    "United Kingdom",
    "Germany",
    "France",
    "Netherlands",
    "Switzerland",
    "Austria",
    "Sweden",
    "Norway",
    "Denmark",
    "Finland",
    "Ireland",
    "Spain",
    "Italy",
    "Poland",
    "Czech Republic",
    "Belgium",
    "Portugal",
    "Other",
  ];

  return (
    <div data-testid="contact-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Get In Touch
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-[#0B2A4A] mb-6 font-['Sora']">
              Start a Conversation
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              Ready to discuss your operational needs? We're here to help you 
              build the infrastructure for growth.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div>
              {isSubmitted ? (
                <div
                  data-testid="contact-success"
                  className="bg-[#F5F7FA] rounded-xl p-8 text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                    Thank You!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Your message has been sent successfully. Our team will be in 
                    touch shortly.
                  </p>
                  <Button
                    data-testid="send-another-message-btn"
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-2 border-[#0B2A4A] text-[#0B2A4A] hover:bg-[#0B2A4A] hover:text-white"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#0B2A4A] font-medium">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-200 focus:ring-[#2F6FED] focus:border-[#2F6FED]"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-[#0B2A4A] font-medium">
                        Company *
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-company-input"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-200 focus:ring-[#2F6FED] focus:border-[#2F6FED]"
                        placeholder="Your company"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#0B2A4A] font-medium">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-200 focus:ring-[#2F6FED] focus:border-[#2F6FED]"
                        placeholder="your.email@company.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-[#0B2A4A] font-medium">
                        Country
                      </Label>
                      <Select onValueChange={handleCountryChange} value={formData.country}>
                        <SelectTrigger
                          data-testid="contact-country-select"
                          className="h-12 border-slate-200 focus:ring-[#2F6FED] focus:border-[#2F6FED]"
                        >
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0B2A4A] font-medium">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="border-slate-200 focus:ring-[#2F6FED] focus:border-[#2F6FED] resize-none"
                      placeholder="Tell us about your operational needs..."
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium py-4 rounded-md transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Request Consultation
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:pl-8">
              <div className="bg-[#0B2A4A] rounded-xl p-8 text-white mb-8">
                <h3 className="text-xl font-semibold mb-6 font-['Sora']">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Email</p>
                      <a
                        href="mailto:contact@vtz-backoffice.com"
                        data-testid="contact-email-link"
                        className="text-[#2F6FED] hover:underline"
                      >
                        contact@vtz-backoffice.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Location</p>
                      <p className="text-slate-300">Southeast Europe</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Business Hours</p>
                      <p className="text-slate-300">
                        Monday - Friday: 9:00 AM - 6:00 PM CET
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Call CTA */}
              <div className="bg-[#F5F7FA] rounded-xl p-8">
                <h3 className="text-xl font-semibold text-[#0B2A4A] mb-4 font-['Sora']">
                  Prefer to Talk?
                </h3>
                <p className="text-slate-600 mb-6">
                  Schedule a 30-minute discovery call to discuss your 
                  operational needs directly with our team.
                </p>
                <Button
                  data-testid="contact-cta-book-call"
                  onClick={openCalendly}
                  className="w-full bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium py-4 rounded-md transition-all duration-200"
                >
                  Schedule a Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
